import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import SortList from '../molecules/SortList';
import DataUpdateTimeDisplay from '../molecules/DataUpdateTimeDisplay';
import Pager from '../molecules/Pager';
import FacilityDetail from '../organisms/FacilityDetail';
import LineTitle from '../atoms/titles/LineTitle';
import PlanListView from '../organisms/PlanListView';
import LinkButton from '../atoms/button/LinkButton';
import Loading from '../atoms/Loading';
import NoResults from '../molecules/NoResults';
import ModalAccommodationConditions from '../modal/ModalAccommodationConditions';
import SearchBoxFacility from '../organisms/SearchBoxFacility';
import SearchConditionSPHotel from '../organisms/SearchConditionSPHotel';
import { getFilghtInputValidationErrors } from '../../modules/flight';
import {
  setSortOrder,
} from '../../modules/hotel';
import {
  fetchPlans,
} from '../../modules/plan';
import { useProgress } from '../../modules/hooks/progress';
import { makeFlightsInput, makePlansInput } from '../../utils/input';
import { scrollToRef } from '../../utils/scroll';
import { BASENAME } from '../../constants';

const selector = (state) => ({
  flight: state.flight,
  hotel: state.hotel,
  plan: state.plan,
});

const totalPlanPerPage = 10;

const PlanList = ({ affiliate, onSearchPlans }) => {
  const { flight, hotel, plan } = useSelector(selector);
  const {
    flightsInput,
    queryParamsValidated: queryParamsValidatedFlight,
    searchedFlightsInput,
    fetchingOutbound,
    fetchingInbound,
    outboundFlights,
    inboundFlights,
  } = flight;
  const {
    hotelsInput,
    queryParamsValidated: queryParamsValidatedHotel,
    fetching: fetchingHotel,
    searchedHotelsInput,
  } = hotel;
  const {
    fetching: fetchingPlan,
    hotelId,
    queryParamsValidated: queryParamsValidatedPlan,
    paginatorInfo,
    plans,
  } = plan;
  const dispatch = useDispatch();
  const sortListRef = useRef(null);

  const [initSearchCompleted, setInitSearchCompleted] = useState(false);

  const searchPlans = useCallback(() => {
    if (onSearchPlans) {
      onSearchPlans();
    }

    dispatch(
      fetchPlans(
        makePlansInput(hotelsInput, flightsInput, hotelId),
        hotelsInput,
        totalPlanPerPage,
        1
      )
    );
  }, [hotelsInput, flightsInput, hotelId]);

  const searchPlansWithCallback = useCallback((callback) => {
    if (onSearchPlans) {
      onSearchPlans();
    }

    dispatch(
      fetchPlans(
        makePlansInput(hotelsInput, flightsInput, hotelId),
        hotelsInput,
        totalPlanPerPage,
        1,
        callback
      )
    );
  }, [hotelsInput, flightsInput, hotelId]);

  const handlePageChange = useCallback((page) => {
    dispatch(
      fetchPlans(
        makePlansInput(hotelsInput, flightsInput, hotelId),
        hotelsInput,
        totalPlanPerPage,
        page
      )
    );

    scrollToRef(sortListRef, true);
  }, [hotelsInput, flightsInput, hotelId, sortListRef]);

  useEffect(() => {
    if (!queryParamsValidatedFlight || !queryParamsValidatedHotel || !queryParamsValidatedPlan) {
      return;
    }

    if (initSearchCompleted) {
      return;
    }

    const { people, airline, outbound } = flightsInput;
    const outboundFlightsInput = makeFlightsInput(people, airline, outbound, outbound.date, true);
    const errorMessages = getFilghtInputValidationErrors(outboundFlightsInput);
    if (errorMessages && errorMessages.length) {
      window.open(`${BASENAME}/search`, '_self');
      return;
    }

    searchPlans();
    setInitSearchCompleted(true);
  }, [queryParamsValidatedFlight, queryParamsValidatedHotel, queryParamsValidatedPlan, flightsInput, initSearchCompleted]);

  const [ modalAccommodation, setModalAccommodation ] = useState(false);
  const toggleModalAccommodation = useCallback(
    () => {
      setModalAccommodation(!modalAccommodation);
    },
    [ modalAccommodation ],
  );

  const selectedHotel = plans && plans.length ? plans[0].hotel : undefined;

  const hasPlans = !!(plans && plans.length);
  const validOutbound = !!(outboundFlights && outboundFlights.length);
  const validInbound = !!(inboundFlights && inboundFlights.length);
  const hasFlights = validInbound && validOutbound;
  const showPlans = affiliate ? hasFlights && hasPlans : hasPlans;
  const fetching = !searchedFlightsInput || !searchedHotelsInput
    || fetchingOutbound || fetchingInbound || fetchingHotel || fetchingPlan;
  const { progressRate } = useProgress(fetchingOutbound, fetchingInbound, fetchingHotel || fetchingPlan);

  const tripStartDate = moment(flightsInput ? flightsInput.outbound.date : undefined);
  const tripEndDate = moment(flightsInput ? flightsInput.inbound.date : undefined);
  const maxStayNights = queryParamsValidatedFlight ? tripEndDate.diff(tripStartDate, 'days') : undefined;

  const dataUpdateTime = !!(plans && plans.length) && plans[0].dataUpdateTime;

  return (
    <React.Fragment>
      <Loading show={fetching} progressRate={searchedFlightsInput ? progressRate : 0} />
      <div className="pagePlanList">
        <FacilityDetail hotel={selectedHotel} />
        <div className="lineTitleWrapper">
          <LineTitle text="プラン一覧" />
        </div>
        <div className="searchBoxLodging">
          <SearchBoxFacility
            hideDestination
            hideTitle
            limitedOptions
            handleSearch={searchPlans}
            maxStayNights={maxStayNights}
          />
        </div>
        <div className="searchBoxLodgingSP">
          <div style={{ padding: 16 }}>
            <SearchConditionSPHotel handleSearch={searchPlans} />
          </div>
          <LinkButton onClick={toggleModalAccommodation}>宿泊条件を変更する</LinkButton>
        </div>
        {showPlans && (
          <div className="sortListWrapper" ref={sortListRef}>
            <SortList
              value={hotelsInput.sortOrder}
              handleChange={(newSortOrder) => {
                dispatch(setSortOrder(newSortOrder));

                if (!paginatorInfo) {
                  return;
                }

                const { currentPage } = paginatorInfo;
                const newhotelsInput = {
                  ...hotelsInput,
                  sortOrder: newSortOrder,
                };

                dispatch(
                  fetchPlans(
                    makePlansInput(newhotelsInput, flightsInput, hotelId),
                    newhotelsInput,
                    totalPlanPerPage,
                    currentPage
                  )
                );
              }}
            />
            {!!dataUpdateTime && (
              <DataUpdateTimeDisplay dataUpdateTime={dataUpdateTime} />
            )}
          </div>
        )}
        {showPlans ? (
          <div className="PlanListWrapper">
            <PlanListView plans={plans} />
          </div>
        ) : (
          <React.Fragment>
            {!fetching && (
              <div style={{ marginTop: 70 }}>
                <NoResults
                  title="ご迷惑をおかけしております"
                  desc="該当のプランが無いか、アクセスが集中して繋がりにくくなっております。<br/>条件を変えていただくか、しばらくして再検索していただけますようお願い申し上げます。"
                />
              </div>
            )}
          </React.Fragment>
        )}
        {showPlans && paginatorInfo && (
          <div className="PagerWrapper">
            <Pager
              currentPage={paginatorInfo.currentPage}
              lastPage={paginatorInfo.lastPage}
              handlePageChange={handlePageChange}
            />
          </div>
        )}
      </div>
      <ModalAccommodationConditions
        open={modalAccommodation}
        onClose={toggleModalAccommodation}
        handleSearch={() => searchPlansWithCallback(toggleModalAccommodation)}
        hideDestination
      />
    </React.Fragment>
  );
};

PlanList.propTypes = {
  onSearchPlans: PropTypes.func,
  affiliate: PropTypes.bool,
};

export default PlanList;
